'use client';

import React from 'react';

import type { TDynamicData } from '../../types/dynamic-data';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { HtmlDescription } from '../shared/html-description';
import { Banner } from '../shared/banner';

import { LuPlus } from 'react-icons/lu';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';

export function ConstructionBudget({ items }: { items: TDynamicData[] }) {
  return (
    <section className={cn('custom-section', 'bg-[#F7F7F7] py-20 sm:py-32 lg:py-40')}>
      <div
        className={cn(
          'custom-container--sm',
          'flex flex-col items-center justify-center gap-10 sm:gap-16 lg:gap-20',
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-10 md:flex-row md:justify-between lg:gap-16 xl:gap-20">
          <h3 className="w-full max-w-[576px] text-4xl font-light text-black sm:text-5xl md:text-6xl lg:text-7xl">
            Čo obsahuje rozpočet stavby
          </h3>
          <ItemsContent items={items} />
        </div>
        <Banner
          title="Chcem získať cenovú ponuku pre rozpočet stavby"
          url="/cennik"
          className={{
            container: 'bg-primary',
            icon: 'text-black',
            title: 'text-black',
            image: 'mix-blend-color-burn',
          }}
        />
      </div>
    </section>
  );
}

function ItemsContent({ items }: { items: TDynamicData[] }) {
  const [isExpanded, setIsExpanded] = React.useState<string | null>(null);

  return (
    <Accordion
      onValueChange={value => setIsExpanded(value)}
      type="single"
      collapsible
      className={cn(
        'flex w-full flex-col items-start justify-start gap-6 sm:gap-8 md:max-w-[580px]',
        'ea-questions__accordion',
      )}
    >
      {items.map(question => (
        <AccordionItem
          key={question.name}
          value={question.name}
          className={cn('w-full border-none', 'ea-questions__item')}
        >
          <AccordionTrigger
            className={cn(
              'relative flex w-full items-center justify-between text-left text-xl font-normal text-black underline sm:text-2xl lg:text-3xl',
              'ea-questions__trigger',
            )}
          >
            <div className="absolute left-0 top-0 flex w-full items-center justify-start">
              <div
                className={cn(
                  'h-[1px] bg-[#000000] transition-all duration-300 ease-in-out',
                  isExpanded === question.name ? 'w-full' : 'w-[33%]',
                )}
              />
              <div
                className={cn(
                  'h-[1px] bg-[#EBE7E7] transition-all duration-300 ease-in-out',
                  isExpanded === question.name ? 'w-0' : 'w-[67%]',
                )}
              />
            </div>
            {question.name}
            <LuPlus
              className={cn(
                'h-6 w-6 text-black transition-all sm:h-8 sm:w-8',
                isExpanded === question.name ? '!rotate-[135deg]' : 'rotate-0',
              )}
            />
          </AccordionTrigger>
          <AccordionContent className="pt-4 sm:pt-6 lg:pt-8">
            <HtmlDescription
              content={question.fields[0]?.value}
              className={cn(
                'w-full font-normal text-black/70',
                'ea-construction-budget__accordion-content',
              )}
              size="sm"
            />
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
